import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AppBreadcrumbs from 'components/AppBreadcrumbs';
import PageLayout from 'layouts/PageLayout';
import NotFound from 'pages/NotFound';
import VerticalTabs from 'components/VerticalTabs';
import { useAsyncFn } from 'react-use';
import { makeStyles } from '@material-ui/core/styles';
import { getTrainingModule, updateTrainingModule } from 'http/trainingModules';
import { useRouteMatch, useHistory, useLocation } from 'react-router-dom';
import { routesAliases } from 'router/routesAliases';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ROLES } from 'appConstants';
import TrainingModuleForm from '../TrainingModuleForm';
import TrainingPartsTable from './TrainingPartsTable';
import TrainingModuleFilesTable from './TrainingModuleFilesTable';
import ModuleTrainersEditor from './ModuleTrainersEditor';
import CompanySyncPanel from 'components/CompanySyncPanel';

const useStyles = makeStyles(theme => ({
  pageContent: {
    padding: theme.spacing(5, 5),
  },
}));

const Edit = ({ routeProps }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const routerMatch = useRouteMatch();
  const { t } = useTranslation('common');
  const role = useSelector(state => state.auth.profile?.role);
  const isHyperAdmin = role === ROLES.HYPER;
  const isSuperAdmin = role === ROLES.SUPER;
  const isAdmin = role === ROLES.ADMIN;
  const { id } = routerMatch.params;
  const [notFoundError, setNotFoundError] = useState(false);
  const [moduleData, setModuleData] = useState(null);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab ?? 0);
  const isSystemModule = Boolean(moduleData?.isFromSystem);
  const canEditSettings = isHyperAdmin || (isSuperAdmin && !isSystemModule);
  const partsReadOnly = isHyperAdmin ? false : isSuperAdmin ? isSystemModule : true;
  const showCompanyMethodToggle = !isHyperAdmin && (isSuperAdmin || isAdmin);
  const canAddModuleFiles = isHyperAdmin ? !isSystemModule : isSuperAdmin || isAdmin;
  const canDeleteModuleFiles = isHyperAdmin ? !isSystemModule : isSuperAdmin || isAdmin;
  const backUrl = routesAliases.trainingModulesList;

  const breadCrumbsItems = [
    {
      title: t(routeProps.parentTitle),
      link: routeProps.parentPath,
    },
    {
      title: moduleData?.name || t(routeProps.title),
      isActive: true,
    },
  ];

  const [fetchState, fetchModule] = useAsyncFn(async moduleId => {
    const result = await getTrainingModule(moduleId);
    if (!result.error) {
      setModuleData(result);
      setNotFoundError(false);
    } else if (result.statusCode === 404) {
      setNotFoundError(true);
    }
    return result;
  }, []);

  const [submittingState, saveModule] = useAsyncFn(async payload => {
    const result = await updateTrainingModule(id, payload);
    if (!result.error && !result.statusCode) {
      history.push(backUrl);
    }
    return result;
  }, [backUrl, history, id]);

  useEffect(() => {
    setNotFoundError(false);
    setModuleData(null);
    fetchModule(id);
  }, [id]);

  useEffect(() => {
    if (location.state?.activeTab !== undefined) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state?.activeTab]);

  const handleTabChange = (event, { tabIndex }) => {
    setActiveTab(tabIndex);
  };

  const tabsData = useMemo(
    () => [
      {
        label: t('trainingModules.tabs.general'),
        alias: 'general',
        Component: (
          <TrainingModuleForm
            mode="edit"
            layout="page"
            initialValues={moduleData}
            backUrl={backUrl}
            submitting={submittingState.loading}
            onSubmitForm={saveModule}
            readOnly={!canEditSettings}
          />
        ),
      },
      {
        label: t('trainingModules.tabs.theory'),
        alias: 'theory',
        Component: (
          <TrainingPartsTable
            trainingModuleId={id}
            trainingModuleCode={moduleData?.code}
            trainingMethod="theory"
            readOnly={partsReadOnly}
            showCompanyMethodToggle={showCompanyMethodToggle}
            canToggleMethod={showCompanyMethodToggle}
          />
        ),
      },
      {
        label: t('trainingModules.tabs.practical'),
        alias: 'practical',
        Component: (
          <TrainingPartsTable
            trainingModuleId={id}
            trainingModuleCode={moduleData?.code}
            trainingMethod="practical"
            readOnly={partsReadOnly}
            showCompanyMethodToggle={showCompanyMethodToggle}
            canToggleMethod={showCompanyMethodToggle}
          />
        ),
      },
      {
        label: t('trainingModules.tabs.ojt'),
        alias: 'ojt',
        Component: (
          <TrainingPartsTable
            trainingModuleId={id}
            trainingModuleCode={moduleData?.code}
            trainingMethod="ojt"
            readOnly={partsReadOnly}
            showCompanyMethodToggle={showCompanyMethodToggle}
            canToggleMethod={showCompanyMethodToggle}
          />
        ),
      },
      {
        label: t('trainingModules.tabs.moduleFiles'),
        alias: 'moduleFiles',
        Component: (
          <TrainingModuleFilesTable
            trainingModuleId={id}
            canAddFiles={canAddModuleFiles}
            canDeleteFiles={canDeleteModuleFiles}
            isSystemModule={isSystemModule}
          />
        ),
      },
      ...(isSuperAdmin
        ? [
            {
              label: t('trainingModules.tabs.trainers'),
              alias: 'trainers',
              Component: <ModuleTrainersEditor trainingModuleId={id} />,
            },
          ]
        : []),
    ],
    [
      backUrl,
      canAddModuleFiles,
      canDeleteModuleFiles,
      canEditSettings,
      id,
      isSuperAdmin,
      moduleData,
      partsReadOnly,
      saveModule,
      showCompanyMethodToggle,
      submittingState.loading,
      t,
    ],
  );

  if (notFoundError) {
    return <NotFound />;
  }

  return (
    <PageLayout
      pageTitle={<AppBreadcrumbs items={breadCrumbsItems} />}
      classes={{ content: classes.pageContent }}
      showLoader={fetchState.loading || !moduleData}
    >
      {moduleData && (
        <>
          {moduleData.isFromSystem ? (
            <CompanySyncPanel entityType="trainingModule" entityId={id} onSynced={() => fetchModule(id)} />
          ) : null}
          <VerticalTabs tabsData={tabsData} activeTab={activeTab} onTabChange={handleTabChange} />
        </>
      )}
    </PageLayout>
  );
};

Edit.propTypes = {
  routeProps: PropTypes.shape({
    parentPath: PropTypes.string,
    parentTitle: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

export default Edit;
